import React, { useState, useEffect } from 'react';
import Header from '../../components/Header/Header';
import authService from '../../services/hybridAuthService';
import { supabase, isSupabaseConfigured } from '../../supabase-client.ts';
import { getEngagementStatsByBook, mergeSummaryReadStat } from '../../services/engagementTrackingService.js';
import './Dashboard.css';

const formatSessionLabel = (count) => `${count} Session${count === 1 ? '' : 's'}`;
const formatSubmissionLabel = (count) => `${count} Prompt${count === 1 ? '' : 's'}`;
const emptyChatStats = () => ({
  totalSessions: 0,
  totalPrompts: 0,
  generalPrompts: 0,
  exploratoryPrompts: 0,
  customPrompts: 0,
  classPromptCount: 0,
  classGeneralModePrompts: 0,
  classExploratoryModePrompts: 0,
  classCustomPromptList: [],
});

const Dashboard = ({ onLogout }) => {
  const [userInfo, setUserInfo] = useState({ email: '', cefr_level: '', userId: '', class_id: null, classIdResolved: false });
  const [accessibleBooks, setAccessibleBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState('');
  const [engagementStats, setEngagementStats] = useState({
    summary_read: 0,
    like_content: 0,
    toggle_content: 0,
    illustration_engaged: 0,
    assessment_complete: 0
  });
  const [chatStats, setChatStats] = useState({
    totalSessions: 0,
    totalPrompts: 0,
    generalPrompts: 0,
    exploratoryPrompts: 0,
    customPrompts: 0,
    classPromptCount: 0,
    classGeneralModePrompts: 0,
    classExploratoryModePrompts: 0,
    classCustomPromptList: [],
  });
  const [chatStatsAvailability, setChatStatsAvailability] = useState({
    user: 'loading',
    class: 'loading',
  });

  useEffect(() => {
    let isMounted = true;
    
    const loadDashboardData = async () => {
      try {
        const profile = await authService.getProfile();
        if (isMounted) {
          setUserInfo({
            email: profile.email || '',
            cefr_level: profile.cefr_level || '',
            userId: profile.id || '',
            class_id: profile.class_id ?? null,
            classIdResolved: true
          });
        }
      } catch (error) {
        console.error('Error fetching profile, fallback to token:', error);

        try {
          const token = localStorage.getItem('token');
          if (token && typeof token === 'string' && token.length > 0) {
            const parts = token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              if (isMounted) {
                setUserInfo({
                  email: payload.email || '',
                  cefr_level: payload.cefr_level || '',
                  userId: payload.id || '',
                  class_id: payload.class_id ?? null,
                  classIdResolved: true
                });
              }
            }
          }
        } catch (tokenError) {
          console.error('Failed to parse token:', tokenError);
          localStorage.removeItem('token');
        }
      }
    };

    loadDashboardData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchAccessibleBooks = async () => {
      if (!isSupabaseConfigured || !supabase) {
        setAccessibleBooks([]);
        setSelectedBookId('');
        return;
      }

      let classId = userInfo.class_id;

      if (!classId && userInfo.email) {
        const { data: userData } = await supabase
          .from('USERS')
          .select('class_id')
          .eq('email', userInfo.email)
          .single();
        classId = userData?.class_id ?? null;
        // Store resolved class_id back to userInfo so UI conditionals react to it
        if (classId || !classId) {
          setUserInfo((prev) => ({ ...prev, class_id: classId, classIdResolved: true }));
        }
      }

      if (!classId) {
        setAccessibleBooks([]);
        setSelectedBookId('');
        return;
      }

      try {
        const { data: accessData, error: accessError } = await supabase
          .from('CLASS_BOOK_ACCESS')
          .select('book_id')
          .eq('class_id', classId);

        if (accessError || !accessData) {
          setAccessibleBooks([]);
          setSelectedBookId('');
          return;
        }

        const uniqueBookIds = [...new Set(accessData.map((row) => row.book_id).filter(Boolean))];

        if (!uniqueBookIds.length) {
          setAccessibleBooks([]);
          setSelectedBookId('');
          return;
        }

        const { data: booksData, error: booksError } = await supabase
          .from('BOOKS')
          .select('book_id, title')
          .in('book_id', uniqueBookIds);

        if (booksError || !booksData) {
          setAccessibleBooks([]);
          setSelectedBookId('');
          return;
        }

        const normalizedBooks = booksData
          .filter((book) => book?.book_id && book?.title)
          .sort((left, right) => left.title.localeCompare(right.title));

        setAccessibleBooks(normalizedBooks);
      } catch (error) {
        console.error('Error fetching accessible books:', error);
        setAccessibleBooks([]);
        setSelectedBookId('');
      }
    };

    fetchAccessibleBooks();
  }, [userInfo.class_id, userInfo.email]);

  useEffect(() => {
    const fetchEngagementStats = async () => {
      if (!userInfo.userId) {
        setEngagementStats({
          summary_read: 0,
          like_content: 0,
          toggle_content: 0,
          illustration_engaged: 0,
          assessment_complete: 0
        });
        return;
      }

      try {
        const selectedBook = accessibleBooks.find((book) => book.book_id === selectedBookId);

        const eventMappedStats = await getEngagementStatsByBook({
          userId: userInfo.userId,
          bookId: selectedBookId,
          bookTitle: selectedBook?.title || '',
        });

        const token = localStorage.getItem('token');
        const API_BASE_URL = (window.ENV && window.ENV.REACT_APP_API_URL) ||
           process.env.REACT_APP_API_URL ||
           (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '');

        if (!token) {
          setEngagementStats(eventMappedStats);
          return;
        }

        const params = new URLSearchParams();
        if (selectedBookId) {
          params.append('book_id', selectedBookId);
        }

        const response = await fetch(`${API_BASE_URL}/api/tracking/stats/me?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          setEngagementStats(eventMappedStats);
          return;
        }

        const result = await response.json();
        const mergedStats = mergeSummaryReadStat(
          eventMappedStats,
          result?.success ? result?.data?.summary_read : 0
        );

        setEngagementStats(mergedStats);
      } catch (error) {
        console.error('Error fetching engagement stats:', error);
        setEngagementStats({
          summary_read: 0,
          like_content: 0,
          toggle_content: 0,
          illustration_engaged: 0,
          assessment_complete: 0
        });
      }
    };

    fetchEngagementStats();
  }, [userInfo.userId, selectedBookId, accessibleBooks]);

  useEffect(() => {
    const fetchChatStats = async () => {
      if (!isSupabaseConfigured || !supabase || !userInfo.userId) {
        setChatStats(emptyChatStats());
        setChatStatsAvailability({ user: 'unavailable', class: 'unavailable' });
        return;
      }

      setChatStatsAvailability({ user: 'loading', class: 'loading' });

      try {
        const [{ data: sessionRows, error: sessionError }, { count: promptCount, error: promptError }] = await Promise.all([
          supabase
            .from('ENGAGEMENT_EVENTS')
            .select('metadata')
            .eq('user_id', userInfo.userId)
            .eq('event_name', 'chat_session_started'),
          supabase
            .from('ENGAGEMENT_EVENTS')
            .select('id', { head: true, count: 'exact' })
            .eq('user_id', userInfo.userId)
            .eq('event_name', 'chat_prompt_submitted'),
        ]);

        if (sessionError || promptError) {
          console.warn('Unable to fetch user chat stats:', { sessionError, promptError });
          setChatStats(emptyChatStats());
          setChatStatsAvailability({ user: 'unavailable', class: 'unavailable' });
          return;
        }

        const uniqueSessionCount = new Set(
          (sessionRows || [])
            .map((row) => row?.metadata?.session_id)
            .filter(Boolean)
        ).size;

        const baseStats = {
          totalSessions: uniqueSessionCount,
          totalPrompts: promptCount || 0,
          generalPrompts: 0,
          exploratoryPrompts: 0,
          customPrompts: 0,
          classPromptCount: 0,
          classGeneralModePrompts: 0,
          classExploratoryModePrompts: 0,
          classCustomPromptList: [],
        };

        setChatStats(baseStats);
        setChatStatsAvailability({ user: 'available', class: 'loading' });

        const API_BASE_URL = (window.ENV && window.ENV.REACT_APP_API_URL) ||
           process.env.REACT_APP_API_URL ||
           (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '');

        const { data: sessionData, error: tokenSessionError } = await supabase.auth.getSession();
        const fallbackToken = localStorage.getItem('token') || '';
        const accessToken = sessionData?.session?.access_token || fallbackToken;

        if (tokenSessionError) {
          console.warn('Supabase session token unavailable; continuing with fallback auth if provided.');
        }

        const classStatsUrl = `${API_BASE_URL}/api/chat-stats/class`;

        const headers = {
          'Content-Type': 'application/json'
        };

        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        const response = await fetch(classStatsUrl, {
          headers
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.warn('Class chat stats request failed:', errorData);
          setChatStatsAvailability((previous) => ({ ...previous, class: 'unavailable' }));
          return;
        }

        const result = await response.json();
        const stats = result?.data || {};

        setChatStats({
          totalSessions: baseStats.totalSessions,
          totalPrompts: baseStats.totalPrompts,
          generalPrompts: stats.generalPrompts || 0,
          exploratoryPrompts: stats.exploratoryPrompts || stats.classExploratoryModePrompts || 0,
          customPrompts: stats.customPrompts || 0,
          classPromptCount: stats.classPromptCount || 0,
          classGeneralModePrompts: stats.classGeneralModePrompts || 0,
          classExploratoryModePrompts: stats.classExploratoryModePrompts || 0,
          classCustomPromptList: Array.isArray(stats.classCustomPromptList)
            ? stats.classCustomPromptList
            : [],
        });
        setChatStatsAvailability({ user: 'available', class: 'available' });
      } catch (error) {
        console.error('Error fetching chat stats:', error);
        setChatStatsAvailability((previous) => ({
          user: previous.user === 'available' ? 'available' : 'unavailable',
          class: 'unavailable',
        }));
      }
    };

    fetchChatStats();
  }, [userInfo.userId]);

  const renderChatStat = (scope, value, formatValue) => {
    if (chatStatsAvailability[scope] === 'loading') return 'Loading...';
    if (chatStatsAvailability[scope] === 'unavailable') return 'Data unavailable';
    return `Total: ${formatValue(value)}`;
  };

  return (
    <div className="dashboard-container">
      <Header userInfo={userInfo} onLogout={onLogout} />
      <div className="dashboard-content">
        <div className="welcome-section">
          <h1>Welcome to ReadSphere!</h1>
          <p>Have a nice day!</p>
        </div>
        
       {/* AI Content Engagement Section */}
        {userInfo.classIdResolved && userInfo.class_id && userInfo.class_id !== 'ac800e1b-18a3-4dfd-9839-778e41489323' && (
          <div className="activity-section">
          <div className="section-header">
            <h2>AI Content Engagement</h2>
          </div>
          
          <div className="dropdown-card">
            <select
              className="book-dropdown"
              value={selectedBookId}
              onChange={(e) => setSelectedBookId(e.target.value)}
            >
              <option value="">All Books</option>
              {accessibleBooks.map((book) => (
                <option key={book.book_id} value={book.book_id}>
                  {book.title}
                </option>
              ))}
            </select>
          </div>

          <div className="activity-grid">
            <div className="activity-card">
              <div className="activity-icon">📖</div>
              <h4>Summary Read</h4>
              <p className="activity-count">{engagementStats.summary_read} Summaries</p>
            </div>
            <div className="activity-card">
              <div className="activity-icon">👍</div>
              <h4>Like Content</h4>
              <p className="activity-count">{engagementStats.like_content} Likes</p>
            </div>
            <div className="activity-card">
              <div className="activity-icon">🔄</div>
              <h4>Toggle Content</h4>
              <p className="activity-count">{engagementStats.toggle_content} Toggles</p>
            </div>
            <div className="activity-card">
              <div className="activity-icon">🖼️</div>
              <h4>Illustration Engaged</h4>
              <p className="activity-count">{engagementStats.illustration_engaged} Illustrations</p>
            </div>
            <div className="activity-card">
              <div className="activity-icon">✅</div>
              <h4>Assessment Complete</h4>
              <p className="activity-count">{engagementStats.assessment_complete} Assessments</p>
            </div>
          </div>
        </div>
        )}
        
         {/* MyReadSphereGPT Section */}
        <div className="gpt-section">
          <h2>MyReadSphereGPT</h2>
          {(chatStatsAvailability.user === 'loading' || chatStatsAvailability.class === 'loading') && (
            <p className="gpt-status-message gpt-status-loading" role="status">Loading GPT activity...</p>
          )}
          {(chatStatsAvailability.user === 'unavailable' || chatStatsAvailability.class === 'unavailable') && (
            <p className="gpt-status-message gpt-status-unavailable" role="status">
              Some GPT activity data is unavailable right now.
            </p>
          )}
          <div className="gpt-group">
            <div className="gpt-group-title">User</div>
            <div className="gpt-grid">
              <div className="gpt-card">
                <h4>Chat Sessions</h4>
                <div className="gpt-info">
                  <span className="info-icon">📥</span>
                   <span className="info-label">{renderChatStat('user', chatStats.totalSessions, formatSessionLabel)}</span>
                </div>
              </div>

              <div className="gpt-card">
                <h4>Prompts Submitted</h4>
                <div className="gpt-info">
                  <span className="info-icon">💬</span>
                   <span className="info-label">{renderChatStat('user', chatStats.totalPrompts, formatSubmissionLabel)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="gpt-group">
            <div className="gpt-group-title">Class</div>
            <div className="gpt-grid">
              <div className="gpt-card">
                <h4>General Prompts</h4>
                <div className="gpt-info">
                  <span className="info-icon">📝</span>
                   <span className="info-label">{renderChatStat('class', chatStats.generalPrompts, formatSubmissionLabel)}</span>
                </div>
              </div>

              <div className="gpt-card">
                <h4>Exploratory Prompts</h4>
                <div className="gpt-info">
                  <span className="info-icon">🧭</span>
                   <span className="info-label">{renderChatStat('class', chatStats.exploratoryPrompts, formatSubmissionLabel)}</span>
                </div>
              </div>

              <div className="gpt-card">
                <h4>Custom Prompts</h4>
                <div className="gpt-info">
                  <span className="info-icon">⭐</span>
                   <span className="info-label">{renderChatStat('class', chatStats.customPrompts, formatSubmissionLabel)}</span>
                </div>
              </div>

              <div className="gpt-card">
                <h4>Total Prompts</h4>
                <div className="gpt-info">
                  <span className="info-icon">🌐</span>
                   <span className="info-label">{renderChatStat('class', chatStats.classPromptCount, formatSubmissionLabel)}</span>
                </div>
              </div>
            </div>

            <div className="class-custom-list-card">
              <h4>Class Custom Prompts (Latest 20)</h4>
              {chatStatsAvailability.class === 'loading' ? (
                <p className="class-custom-empty">Loading class custom prompts...</p>
              ) : chatStatsAvailability.class === 'unavailable' ? (
                <p className="class-custom-empty">Class prompt data is unavailable.</p>
              ) : chatStats.classCustomPromptList.length === 0 ? (
                <p className="class-custom-empty">No class custom prompts yet.</p>
              ) : (
                <div className="class-custom-list">
                  {chatStats.classCustomPromptList.map((item, index) => (
                    <div className="class-custom-item" key={`${item.created_at || 'na'}-${item.user_id || 'na'}-${index}`}>
                      <p className="class-custom-text">{item.prompt_text}</p>
                      <p className="class-custom-meta">
                        {(item.user_email || item.user_id || 'Unknown user')} {item.created_at ? `- ${new Date(item.created_at).toLocaleString()}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
